import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { MatMenuModule, MatMenu } from '@angular/material/menu';
import { ImageLoaderComponent } from '../../../image-loader/image-loader.component';
import {
  Hub,
  SocialNetworkEntityType,
  SocialNetworkInstanceImageType,
} from '@meplis/common';
import { SocialNetworkInstanceImage } from 'libs/meplis/src/lib/common/models/social-network/social-network-instance-image';
import { ModalService } from '@meplis/services';
import { ExploreMeplisModalComponent } from '../../../explore-meplis-modal/explore-meplis-modal.component';
import { ApiSocialV2Service } from '@cnp/services';
import { SocialService } from 'apps/meplis-cnp/src/app/shared/services/application/social.service';
import { AuthenticationService } from 'apps/meplis-cnp/src/app/shared/services/application/authentication.service';
import { AppRoutes } from '@cnp/common';
import { Subscription } from 'rxjs';
import { HubService } from 'apps/meplis-cnp/src/app/shared/services/application/hub.service';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
  selector: 'app-hub-menu',
  standalone: true,
  imports: [CommonModule, TranslatePipe, MatMenuModule, ImageLoaderComponent, FaIconComponent],
  templateUrl: './hub-menu.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './hub-menu.component.scss',
})
export class HubMenuComponent implements OnInit, OnChanges, OnDestroy {
  private _router = inject(Router);
  private _modalService = inject(ModalService);
  private _apiSocialV2Service = inject(ApiSocialV2Service);
  private _socialService = inject(SocialService);
  private _authService = inject(AuthenticationService);
  private _hubService = inject(HubService);

  @Input() public currentHub: Hub;
  @Input() public showExploreMeplis: boolean = true;
  @Input() public showGoHome: boolean = true;
  @Input() public triggerStyle: 'workspace' | 'general' | 'mobile' = 'general';

  @Output() public hubSelected = new EventEmitter<void>();

  @ViewChild('hubMenu', { static: true }) public menu: MatMenu;

  public otherHubs: Hub[] = [];
  public imageTypes = SocialNetworkInstanceImageType;
  public userInstanceId: string;
  public isGlobalUser: boolean = false;
  public isHubsRouteActive: boolean = false;
  public _allHubs: Hub[] = [];
  private _inputCurrentHub: Hub;
  private _routeSubscription: Subscription;
  private _followHubChangedSubscription: Subscription;

  public ngOnInit(): void {
    this._updateHubsRouteActive(this._router.url);

    this._routeSubscription = this._router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this._updateHubsRouteActive(event.urlAfterRedirects);
      }
    });

    if (this._authService.isLogged) {
      this._socialService.getUserInstance().subscribe((userInstance) => {
        this.userInstanceId = userInstance?.token;
        this.isGlobalUser =
          userInstance?.type === SocialNetworkEntityType.GLOBAL ||
          !userInstance?.type;
        this._tryGetOtherHubs();
      });
    }

    this._followHubChangedSubscription =
      this._hubService.followHubChanged.subscribe(() =>
        this._tryGetOtherHubs()
      );
  }

  public ngOnDestroy(): void {
    this._routeSubscription?.unsubscribe();
    this._followHubChangedSubscription?.unsubscribe();
  }

  private _updateHubsRouteActive(url: string): void {
    const route = url.replace(/^\//, '').split('?')[0];
    this.isHubsRouteActive = route === 'hubs' || route.startsWith('hubs/');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentHub']) {
      this._inputCurrentHub = this.currentHub;
      this._filterOtherHubs();
      this._tryGetOtherHubs();
    }
  }

  private _tryGetOtherHubs(): void {
    if (this.userInstanceId) {
      this._getOtherHubs();
    }
  }

  public getLogoImage(hub: Hub): SocialNetworkInstanceImage {
    return hub?.instance?.images?.find(
      (img) => img.type === SocialNetworkInstanceImageType.LOGO
    );
  }

  public goHome(): void {
    if (this.currentHub) {
      const hubSlug = this.currentHub.slug || this.currentHub.token;
      this._router.navigate([`hubs/${hubSlug}/${this.currentHub.token}`]);
    }
  }

  private _getOtherHubs(): void {
    this._apiSocialV2Service
      .getUserHubs(this.userInstanceId)
      .subscribe((hubs) => {
        this._allHubs = hubs; //.filter((hub) => hub.following);

        // Auto-select currentHub from the first hub if not provided via @Input
        if (!this.currentHub && hubs.length) {
          this.currentHub = this._allHubs[0];
        }

        this._filterOtherHubs();
      });
  }

  private _filterOtherHubs(): void {
    this.otherHubs = this.currentHub?.token
      ? this._allHubs.filter((hub) => hub.token !== this.currentHub.token)
      : this._allHubs;
  }

  public selectHub(hub: Hub): void {
    this.currentHub = hub;
    this._filterOtherHubs();
    const hubSlug = hub.slug || hub.token;
    this._router.navigate([`hubs/${hubSlug}/${hub.token}`]);
    this.hubSelected.emit();
  }

  public exploreWorkspaces(): void {
    this._router.navigate([AppRoutes['HUBS'].fullPath]);
    this.hubSelected.emit();
  }

  public exploreMeplis(): void {
    this._modalService.openModal(
      ExploreMeplisModalComponent,
      {
        userInstanceId: this.userInstanceId,
      },
      undefined,
      500
    );
  }
}
